import React, { useState } from 'react';
import { doc, updateDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import SheetModal, { sheetButtons } from '../common/SheetModal';
import { EQUIPMENT_OPTIONS, toggleEquipment } from '../../utils/loadingEquipment';
import { VEHICLE_TYPES, FUEL_TYPES, validateVehicle, normalizeVehicle, mergeDriverEquipment } from '../../utils/vehicleProfile';
import { COLORS } from '../../styles/tokens';
import { categoryAxles, supportsTrailer } from '../../utils/truckCatalog';

// Bunnark for å endre kjøretøyet (reg.nr., type, kapasitet, drivstoff) og lasteutstyret.
// vehicle: eksisterende kjøretøy ({ id, ...felt }) eller null for å registrere et nytt.
// onSaved får det lagrede kjøretøyet (med id).
export default function VehicleSheet({ vehicle, onClose, onSaved }) {
  const { currentUser, userDoc, refreshUserDoc } = useAuth();
  const [form, setForm] = useState(() => ({
    type: vehicle?.type ?? 'tipper',
    axles: vehicle?.axles != null ? String(vehicle.axles) : '',
    capacityTons: vehicle?.capacityTons != null ? String(vehicle.capacityTons) : '',
    fuelType: vehicle?.fuelType ?? 'diesel',
    registrationPlate: vehicle?.registrationPlate ?? '',
    loadingEquipment: Array.isArray(vehicle?.loadingEquipment) ? vehicle.loadingEquipment : [],
    hasTrailer: vehicle?.hasTrailer === true,
  }));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const set = field => e => setForm(f => {
    const next = { ...f, [field]: e.target.value };
    if (field === 'type' && !categoryAxles(next.type).includes(Number(next.axles))) next.axles = '';
    if (field === 'type' && !supportsTrailer(next.type)) next.hasTrailer = false;
    return next;
  });

  async function handleSave(e) {
    e.preventDefault();
    const found = validateVehicle(form);
    setErrors(found);
    setFailed(false);
    if (Object.keys(found).length > 0 || !currentUser) return;
    const data = normalizeVehicle(form);
    setSaving(true);
    try {
      let id = vehicle?.id;
      if (id) {
        await updateDoc(doc(db, 'vehicles', id), { ...data, updatedAt: serverTimestamp() });
      } else {
        const ref = await addDoc(collection(db, 'vehicles'), { driverUid: currentUser.uid, ...data, createdAt: serverTimestamp() });
        id = ref?.id;
      }
      // Varselmatchingen leser users.equipment: kjøretøytypen legges til uten å fjerne eksisterende valg.
      // Feiler dette, er kjøretøyet likevel lagret.
      try {
        const merged = mergeDriverEquipment(userDoc?.equipment, data.type);
        if (merged.length !== (Array.isArray(userDoc?.equipment) ? userDoc.equipment.length : 0)) {
          await updateDoc(doc(db, 'users', currentUser.uid), { equipment: merged });
          await refreshUserDoc?.();
        }
      } catch { /* ignorer */ }
      onSaved({ ...(vehicle ?? { driverUid: currentUser.uid }), ...data, id });
      onClose();
    } catch {
      setFailed(true);
      setSaving(false);
    }
  }

  return (
    <SheetModal onClose={onClose} busy={saving} label="Kjøretøy">
      <h2 style={sheetButtons.title}>{vehicle ? 'Endre kjøretøy' : 'Legg til kjøretøy'}</h2>
      <form onSubmit={handleSave} noValidate>
        <div style={styles.field}>
          <label htmlFor="veh-plate" style={styles.label}>Registreringsnummer</label>
          <input id="veh-plate" value={form.registrationPlate} onChange={set('registrationPlate')} maxLength={12}
                 autoCapitalize="characters" placeholder="AB 12345"
                 aria-invalid={!!errors.registrationPlate} aria-describedby={errors.registrationPlate ? 'veh-plate-err' : undefined}
                 style={{ ...styles.input, ...(errors.registrationPlate ? styles.inputError : {}) }} />
          {errors.registrationPlate && <div id="veh-plate-err" role="alert" style={styles.fieldError}>{errors.registrationPlate}</div>}
        </div>

        <div style={styles.field}>
          <label htmlFor="veh-type" style={styles.label}>Kjøretøytype</label>
          <select id="veh-type" value={form.type} onChange={set('type')} style={styles.input}>
            {VEHICLE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        {categoryAxles(form.type).length > 0 && (
          <div style={styles.field}>
            <label htmlFor="veh-axles" style={styles.label}>Antall aksler</label>
            <select id="veh-axles" value={form.axles} onChange={set('axles')}
                    aria-invalid={!!errors.axles} aria-describedby={errors.axles ? 'veh-axles-err' : undefined}
                    style={{ ...styles.input, ...(errors.axles ? styles.inputError : {}) }}>
              <option value="">Velg</option>
              {categoryAxles(form.type).map(a => <option key={a} value={String(a)}>{a}</option>)}
            </select>
            {errors.axles && <div id="veh-axles-err" role="alert" style={styles.fieldError}>{errors.axles}</div>}
          </div>
        )}

        {supportsTrailer(form.type) && (
          <div style={styles.field}>
            <label style={styles.check}>
              <input type="checkbox" checked={form.hasTrailer}
                     onChange={e => setForm(f => ({ ...f, hasTrailer: e.target.checked }))} />
              {' '}Har henger
            </label>
            <p style={styles.hint}>Brukes til å vise deg oppdrag som krever bil med henger.</p>
          </div>
        )}

        <div style={styles.field}>
          <label htmlFor="veh-cap" style={styles.label}>Kapasitet (tonn)</label>
          <input id="veh-cap" value={form.capacityTons} onChange={set('capacityTons')} inputMode="decimal"
                 aria-invalid={!!errors.capacityTons} aria-describedby={errors.capacityTons ? 'veh-cap-err' : undefined}
                 style={{ ...styles.input, ...(errors.capacityTons ? styles.inputError : {}) }} />
          {errors.capacityTons && <div id="veh-cap-err" role="alert" style={styles.fieldError}>{errors.capacityTons}</div>}
        </div>

        <div style={styles.field}>
          <label htmlFor="veh-fuel" style={styles.label}>Drivstoff</label>
          <select id="veh-fuel" value={form.fuelType} onChange={set('fuelType')} style={styles.input}>
            {FUEL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        <div style={styles.field} role="group" aria-labelledby="veh-equip-label">
          <div id="veh-equip-label" style={styles.label}>Bilens utstyr</div>
          <p style={styles.hint}>Utstyret du har med på bilen og kan laste med selv.</p>
          <div style={styles.chips}>
            {EQUIPMENT_OPTIONS.map(o => {
              const on = form.loadingEquipment.includes(o.value);
              return (
                <button
                  key={o.value} type="button" role="checkbox" aria-checked={on}
                  onClick={() => setForm(f => ({ ...f, loadingEquipment: toggleEquipment(f.loadingEquipment, o.value) }))}
                  style={{ ...styles.chip, ...(on ? styles.chipOn : {}) }}
                >
                  {on ? '✓ ' : ''}{o.label}
                </button>
              );
            })}
          </div>
        </div>

        {failed && <p role="alert" style={sheetButtons.error}>Kunne ikke lagre kjøretøyet. Prøv igjen.</p>}
        <button type="submit" style={sheetButtons.gold} disabled={saving}>{saving ? 'Lagrer…' : 'Lagre'}</button>
        <button type="button" style={sheetButtons.ghost} onClick={onClose} disabled={saving}>Avbryt</button>
      </form>
    </SheetModal>
  );
}

const styles = {
  field:      { marginTop: 12 },
  check:      { display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: COLORS.textDark },
  label:      { display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.textMid, marginBottom: 5 },
  hint:       { fontSize: 12.5, color: COLORS.textLight, margin: '0 0 8px', lineHeight: 1.4 },
  input:      { width: '100%', boxSizing: 'border-box', height: 48, padding: '0 14px', borderRadius: 14, border: `1px solid ${COLORS.border}`, fontSize: 15.5, fontFamily: 'inherit', background: COLORS.white, color: COLORS.textDark },
  inputError: { borderColor: COLORS.danger },
  fieldError: { fontSize: 12, color: COLORS.dangerText, marginTop: 4 },
  chips:      { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 },
  chip:       { minHeight: 40, padding: '0 14px', borderRadius: 20, borderWidth: 1.5, borderStyle: 'solid', borderColor: COLORS.border, background: COLORS.white, color: COLORS.textDark, fontSize: 14, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' },
  chipOn:     { borderColor: COLORS.accent, background: COLORS.surfaceHover },
};
